/**
 * One case: its input and expectations, then the arms side by side, so they can be compared
 * on the same input. Each arm shows one trial at a time, the first failed one by default;
 * "Show all trials" stacks them. Trials of different arms are repeats, not pairs, so each
 * arm picks its own.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { EvalCaseSnapshot, EvalModel, EvalSuggestion, RunEvalArmInfo, StoredTrialResult } from '../../types/evalTypes';
import { trialCost } from '../../utils/evalCost';
import { formatPassRate } from '../../utils/evalFormat';
import { ArmLabel } from '../ArmLabel';
import { CaseExpectView, CaseInputView } from './CaseViews';
import { TrialCard } from './TrialCard';
import { defaultTrial, TrialPicker } from './TrialPicker';

interface CaseDetailProps {
  suiteId: string;
  caseId: string;
  /** The case version the run used. */
  caseInfo: EvalCaseSnapshot | undefined;
  arms: RunEvalArmInfo[];
  /** The suite's registry model, to mark the production arm. */
  productionModel: string | undefined;
  results: StoredTrialResult[];
  models: EvalModel[];
  selectedArmId: string | null;
  currentUid: string | undefined;
  onSaveFlag: (resultId: string, checkIds: string[], note: string) => Promise<void>;
  suggestions: EvalSuggestion[];
  onDecideSuggestion: (suggestionId: string, accept: boolean, rejectReason?: string) => Promise<void>;
}

export const CaseDetail = ({
  suiteId,
  caseId,
  caseInfo,
  arms,
  productionModel,
  results,
  models,
  selectedArmId,
  currentUid,
  onSaveFlag,
  suggestions,
  onDecideSuggestion,
}: CaseDetailProps) => {
  const caseResults = results.filter((result) => result.caseId === caseId);
  const [showAll, setShowAll] = useState(false);
  // By `case~arm`, so each case keeps its own choice while the page is open.
  const [picked, setPicked] = useState<Record<string, string>>({});

  return (
    <div id="case-detail" className="bg-white rounded-lg shadow-sm p-4 space-y-4 scroll-mt-24">
      <div>
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-lg font-semibold text-gray-900 font-mono">{caseId}</h2>
          {caseInfo && <span className="text-sm text-gray-500">v{caseInfo.number}</span>}
          <Link to={`/evals/cases?suite=${suiteId}&case=${caseId}`} className="text-sm text-blue-600 hover:underline">
            Open in Cases
          </Link>
        </div>
        {caseInfo && <p className="text-sm text-gray-500">{caseInfo.description}</p>}
      </div>

      {caseInfo ? (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Input</h3>
            <div className="max-h-[32rem] overflow-y-auto pr-1">
              <CaseInputView suiteId={suiteId} input={caseInfo.input} />
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Expected</h3>
            <CaseExpectView suiteId={suiteId} expect={caseInfo.expect} />
          </div>
        </div>
      ) : (
        <p className="text-sm text-amber-800">This run did not save this case.</p>
      )}

      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Outputs by arm</h3>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)} />
          Show all trials
        </label>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${arms.length}, minmax(16rem, 1fr))` }}>
        {arms.map((arm) => {
          const armResults = caseResults.filter((result) => result.armId === arm.id).sort((a, b) => a.trial - b.trial);
          const ran = armResults.filter((result) => result.status !== 'skipped');
          const passed = ran.filter((result) => result.passed).length;
          const pickKey = `${caseId}~${arm.id}`;
          const selected = armResults.find((result) => result.resultId === picked[pickKey]) ?? defaultTrial(armResults);
          const shown = showAll ? armResults : selected ? [selected] : [];
          return (
            // The arm picked in the matrix has a blue border.
            <div key={arm.id} className={`rounded-lg border bg-white ${arm.id === selectedArmId ? 'border-blue-400 ring-1 ring-blue-400' : 'border-gray-200'}`}>
              <div className="px-3 pt-3 pb-2 space-y-2 border-b border-gray-200">
                <div className="flex items-start justify-between gap-2 text-sm">
                  <ArmLabel arm={arm} models={models} productionModel={productionModel} />
                  <span className="text-xs text-gray-700 whitespace-nowrap">passed {formatPassRate(passed, ran.length)}</span>
                </div>
                {!showAll && armResults.length > 1 && (
                  <TrialPicker
                    results={armResults}
                    selectedId={selected?.resultId}
                    suggestions={suggestions}
                    onSelect={(resultId) => setPicked((current) => ({ ...current, [pickKey]: resultId }))}
                  />
                )}
              </div>
              <div className="divide-y divide-gray-200">
                {shown.map((result) => (
                  <TrialCard
                    key={result.resultId}
                    result={result}
                    cost={trialCost(models, result)}
                    currentUid={currentUid}
                    onSaveFlag={(checkIds, note) => onSaveFlag(result.resultId, checkIds, note)}
                    suggestions={suggestions.filter(
                      (suggestion) => suggestion.status === 'pending' && suggestion.kind === 'judge-flag' && suggestion.resultId === result.resultId,
                    )}
                    onDecideSuggestion={onDecideSuggestion}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
